'use client';

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { isCapacitorNative } from '@/lib/capacitor-runtime';
import {
  applyCapacitorStatusBarTheme,
  nativeStatusBarForTheme,
  scheduleCapacitorStatusBarSync,
} from '@/lib/native-status-bar-colors';
import { safeStorageGet, safeStorageSet } from '@/lib/storage';
import {
  parseStoredThemePreference,
  readSystemPrefersDark,
  resolveThemeMode,
  THEME_STORAGE_KEY,
  type ThemeMode,
} from '@/lib/theme-preference';

export type { ThemeMode };

interface ThemeContextValue {
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function applyThemeToDocument(theme: ThemeMode) {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
  root.dataset.theme = theme;
  root.style.colorScheme = theme;

  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute('content', nativeStatusBarForTheme(theme).bg);
  }

  if (isCapacitorNative()) {
    void applyCapacitorStatusBarTheme(theme).catch(() => {});
    scheduleCapacitorStatusBarSync();
  }
}

function persistThemePreference(theme: ThemeMode) {
  safeStorageSet(THEME_STORAGE_KEY, theme);
}

function readInitialTheme(): ThemeMode {
  const stored = parseStoredThemePreference(safeStorageGet(THEME_STORAGE_KEY));
  return resolveThemeMode(stored, readSystemPrefersDark());
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ThemeMode>(() => readInitialTheme());

  useEffect(() => {
    const nextTheme = readInitialTheme();
    setThemeState(nextTheme);
    applyThemeToDocument(nextTheme);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    if (parseStoredThemePreference(safeStorageGet(THEME_STORAGE_KEY)) !== null) return;

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const syncWithSystem = () => {
      if (parseStoredThemePreference(safeStorageGet(THEME_STORAGE_KEY)) !== null) return;
      const nextTheme = resolveThemeMode(null, media.matches);
      setThemeState(nextTheme);
      applyThemeToDocument(nextTheme);
    };

    media.addEventListener('change', syncWithSystem);
    return () => media.removeEventListener('change', syncWithSystem);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      setTheme: (nextTheme) => {
        setThemeState(nextTheme);
        persistThemePreference(nextTheme);
        applyThemeToDocument(nextTheme);
      },
      toggleTheme: () => {
        const nextTheme: ThemeMode = theme === 'dark' ? 'light' : 'dark';
        setThemeState(nextTheme);
        persistThemePreference(nextTheme);
        applyThemeToDocument(nextTheme);
      },
    }),
    [theme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
